import { useEffect, useRef, useState } from 'react';
import { baseURL } from '../../../core/api/http-methods';

export type ApprovalEventSource = 'fresh' | 'resumed';
export type PollStopReason =
  'active' | 'failed' | 'not_found' | 'timed_out' | 'closed' | 'unmounted';

export type ApprovalEvent =
  | { event: 'approval_shown'; source: ApprovalEventSource }
  | { event: 'tab_hidden' }
  | { event: 'tab_visible'; secondsHidden: number }
  | { event: 'timed_out' }
  | { event: 'check_again' }
  | { event: 'poll_stopped'; reason: PollStopReason; polls: number }
  | { event: 'dialog_closed'; lastStatus: string };

const EVENT_PATH = '/payments-consent/client-event';

/**
 * Reports one approval-view event to the backend log. Fire and forget: it never throws, never
 * surfaces an error, and retries a failed send at most once. `keepalive` lets the request
 * outlive a tab that is going to the background (the customer switching to their banking app).
 * Auth matches the shared API client: a bearer token read from localStorage, sent only when
 * one is stored. The registration waiting page runs before any session exists; it must send no
 * Authorization header at all rather than a literal "Bearer null".
 */
export function sendApprovalEvent(ref: string, evt: ApprovalEvent): void {
  const { event, ...extra } = evt;
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  const body = JSON.stringify({
    ref,
    event,
    at: new Date().toISOString(),
    ...extra
  });
  const attempt = () =>
    fetch(baseURL + EVENT_PATH, {
      method: 'POST',
      body,
      keepalive: true,
      headers
    }).then((res) => {
      if (!res.ok) throw new Error(`client-event ${res.status}`);
    });
  try {
    attempt().catch(() => {
      try {
        attempt().catch(() => {});
      } catch {
        /* never surface */
      }
    });
  } catch {
    /* never surface */
  }
}

interface UseApprovalEventsArgs {
  ref: string;
  source: ApprovalEventSource;
  /** Whether the approval view is open. Events are only sent while it is. */
  open: boolean;
  /** True once the on-screen countdown has reached zero. */
  timedOut: boolean;
}

/** Mutable per-mount state, kept in one ref so effects and handlers read it without deps. */
interface ApprovalTracker {
  ref: string;
  source: ApprovalEventSource;
  open: boolean;
  lastStatus: string;
  polls: number;
  /** A polling run is in progress and has not yet reported its `poll_stopped`. */
  polling: boolean;
  /** The customer closed the view; nothing more is sent from this mount. */
  closed: boolean;
  hiddenAt: number | null;
  /** The mount effect has been cleaned up and its unmount report is still queued. */
  detachPending: boolean;
}

export interface ApprovalEvents {
  /** Call once at the start of every poll. */
  countPoll: () => void;
  /** Records the latest consent status the poll observed. */
  setLastStatus: (status: string) => void;
  /** Reports why polling ended; a no-op if this run already reported. */
  stopPoll: (reason: PollStopReason) => void;
  /** "Check again" pressed: reports it and begins a new polling run. */
  checkAgain: () => void;
  /**
   * The customer is closing the view. Call it synchronously from the close handler, before the
   * parent removes the view: a parent that unmounts the view in the same render as the close
   * never lets an effect observe the close. Sends `dialog_closed` and `poll_stopped(closed)`
   * once; a later unmount then reports nothing.
   */
  close: () => void;
}

const emitFrom = (t: ApprovalTracker, evt: ApprovalEvent) => {
  if (!t.open || t.closed) return;
  sendApprovalEvent(t.ref, evt);
};

const stopPollFrom = (t: ApprovalTracker, reason: PollStopReason) => {
  if (!t.polling) return;
  t.polling = false;
  sendApprovalEvent(t.ref, { event: 'poll_stopped', reason, polls: t.polls });
};

/**
 * Shared by both approval views. Emits the lifecycle events (shown, tab hidden/visible, timed
 * out, check again, closed) and a single `poll_stopped` per polling run, counting the polls
 * made during this mount.
 *
 * Only an explicit `close()` counts as the customer closing the view. The view going away for
 * any other reason (a result screen replacing it, navigating elsewhere) reports
 * `poll_stopped(unmounted)` if a polling run was still open, and never `dialog_closed`.
 *
 * Safe under React StrictMode's mount, unmount, remount cycle: the unmount report is deferred
 * by a microtask and cancelled when the same instance remounts synchronously, so a development
 * double mount sends one `approval_shown` and no spurious `poll_stopped(unmounted)`, and the
 * remounted instance keeps its polling state.
 */
export function useApprovalEvents({
  ref,
  source,
  open,
  timedOut
}: UseApprovalEventsArgs): ApprovalEvents {
  const trackerRef = useRef<ApprovalTracker>({
    ref,
    source,
    open,
    lastStatus: 'UNKNOWN',
    polls: 0,
    polling: false,
    closed: false,
    hiddenAt: null,
    detachPending: false
  });
  trackerRef.current.ref = ref;
  trackerRef.current.source = source;
  trackerRef.current.open = open;

  useEffect(() => {
    const t = trackerRef.current;
    if (t.detachPending) {
      // The same instance remounting straight after its cleanup (StrictMode): keep its state
      // and cancel the queued unmount report.
      t.detachPending = false;
    } else {
      t.lastStatus = 'UNKNOWN';
      t.polls = 0;
      t.polling = t.open;
      t.closed = false;
      t.hiddenAt = null;
      emitFrom(t, { event: 'approval_shown', source: t.source });
    }
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        t.hiddenAt = Date.now();
        emitFrom(t, { event: 'tab_hidden' });
      } else {
        const since = t.hiddenAt;
        t.hiddenAt = null;
        emitFrom(t, {
          event: 'tab_visible',
          secondsHidden:
            since === null ? 0 : Math.round((Date.now() - since) / 1000)
        });
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      t.detachPending = true;
      queueMicrotask(() => {
        if (!t.detachPending) return;
        t.detachPending = false;
        // Polling still running when the view goes away without a close or a result.
        stopPollFrom(t, 'unmounted');
      });
    };
  }, []);

  useEffect(() => {
    if (!timedOut) return;
    const t = trackerRef.current;
    emitFrom(t, { event: 'timed_out' });
    stopPollFrom(t, 'timed_out');
  }, [timedOut]);

  const [api] = useState<ApprovalEvents>(() => ({
    countPoll: () => {
      trackerRef.current.polls += 1;
    },
    setLastStatus: (status: string) => {
      trackerRef.current.lastStatus = status;
    },
    stopPoll: (reason: PollStopReason) =>
      stopPollFrom(trackerRef.current, reason),
    checkAgain: () => {
      const t = trackerRef.current;
      if (!t.open || t.closed) return;
      emitFrom(t, { event: 'check_again' });
      t.polling = true;
    },
    close: () => {
      const t = trackerRef.current;
      if (!t.open || t.closed) return;
      emitFrom(t, { event: 'dialog_closed', lastStatus: t.lastStatus });
      t.closed = true;
      stopPollFrom(t, 'closed');
    }
  }));
  return api;
}
